import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useLocaleContext } from "src/context/locale-context";
import { ProductSection } from "src/components/features/home/ProductSection/ProductSection";
import { BusIcon } from "src/components/features/home/ProductSection/icons/BusIcon";
import { BusTourFeaturedCard, BusTourGridCard } from "./BusTourCards";
import { LeftWaves, RightWaves } from "./BusToursDecoration";
import { BUS_TOUR_BADGES, getBusToursText } from "./busToursText";
import classes from "./BusToursCarousel.module.css";

const PAGE_SIZE = 5; // 1 featured + 4 grid cards per page

// Light blue diagonal gradient, full section height — the decor layer below carries this (not
// the shared ProductSection accentColor fade), so the waves and the base color live in one
// coherent layer with a single fade-to-neighbor at top/bottom.
const SECTION_STYLE = {
  background: "transparent",
};

// One decorative layer (position:absolute; inset:0) instead of scattering pieces directly on the
// section — simple soft waves only (left side, running behind the featured card; bottom-right,
// diagonal), no landmarks/vehicles/routes per request. Center stays calm/undecorated.
const DECOR = (
  <div className={classes.decorLayer} aria-hidden="true">
    <LeftWaves className={classes.leftWaves} />
    <RightWaves className={classes.rightWaves} />
  </div>
);

function chunk(list, size) {
  const pages = [];
  for (let i = 0; i < list.length; i += size) {
    pages.push(list.slice(i, i + size));
  }
  return pages;
}

export function BusToursCarousel({ data }) {
  const { getStringRes } = useLocaleContext();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(0);

  const text = (key) => getBusToursText(getStringRes, key);
  const pages = useMemo(() => chunk(data ?? [], PAGE_SIZE), [data]);

  if (pages.length === 0) return null;

  const page = pages[Math.min(activePage, pages.length - 1)];
  const [featured, ...gridTours] = page;

  return (
    <ProductSection
      sectionStyle={SECTION_STYLE}
      sectionClassName={classes.section}
      decor={DECOR}
      icon={<BusIcon size={72} />}
      title={text("home.section.bus.title")}
      subtitle={text("home.section.bus.subtitle")}
      viewAllLabel={text("home.section.viewAll")}
      onViewAll={() => navigate("/bus-tours")}
      canPrev={activePage > 0}
      canNext={activePage < pages.length - 1}
      onPrev={pages.length > 1 ? () => setActivePage((p) => Math.max(0, p - 1)) : undefined}
      onNext={
        pages.length > 1 ? () => setActivePage((p) => Math.min(pages.length - 1, p + 1)) : undefined
      }
      dotCount={pages.length}
      activeDot={activePage}
      onDotClick={setActivePage}>
      <div className={classes.layout}>
        <div className={classes.featuredWrap}>
          <BusTourFeaturedCard tour={featured} badge={BUS_TOUR_BADGES[0]} />
        </div>
        <div className={classes.grid}>
          {gridTours.map((tour, i) => (
            <BusTourGridCard key={tour.code} tour={tour} badge={BUS_TOUR_BADGES[i + 1]} />
          ))}
        </div>
      </div>
    </ProductSection>
  );
}
